import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import { st, classes } from './AnnouncementModalLayout.st.css';
import TextButton from '../TextButton';
import { dataHooks } from './constants';

import BaseModalLayout from '../BaseModalLayout';
import Button from '../Button';
import { AnnouncementModalLayoutProps } from './AnnouncementModalLayout.types';
import deprecationLog from '../utils/deprecationLog';

/** A layout for announcement modals, to be used inside a &lt;Modal /&gt; */
const AnnouncementModalLayout = ({
  theme = 'standard', // TODO: add default value for skin once theme is removed
  actionsSize = 'medium',
  ...propsWithoutDefaults
}: AnnouncementModalLayoutProps) => {
  const { className, children, footnoteSkin, ...restProps } = {
    theme,
    actionsSize,
    ...propsWithoutDefaults,
  };
  const skin = propsWithoutDefaults.skin || theme;

  useEffect(() => {
    if (theme) {
      deprecationLog(
        '<AnnouncementModalLayout/> - prop "theme" is deprecated and will be removed in next major release, please use "skin" property instead.',
      );
    }
  }, [theme]);

  return (
    <BaseModalLayout
      className={st(classes.announcementModalLayout, className)}
      {...restProps}
      skin={skin}
    >
      <BaseModalLayout.Illustration />
      <BaseModalLayout.Header titleSize="extraLarge" />

      <BaseModalLayout.Content hideTopScrollDivider hideBottomScrollDivider>
        {children}
      </BaseModalLayout.Content>

      <BaseModalLayout.Footer />
      <Link {...restProps} skin={skin} />
      <div className={classes.bottomSpacing} />
      <BaseModalLayout.Footnote skin={footnoteSkin} />
    </BaseModalLayout>
  );
};

const Link = ({
  linkText,
  linkOnClick,
  skin,
}: Partial<AnnouncementModalLayoutProps>) =>
  ((linkText || linkOnClick) && (
    <div className={classes.link}>
      <TextButton
        size="small"
        weight="normal"
        dataHook={dataHooks.link}
        onClick={linkOnClick}
        skin={skin}
      >
        {linkText}
      </TextButton>
    </div>
  )) ||
  null;

AnnouncementModalLayout.displayName = 'AnnouncementModalLayout';

AnnouncementModalLayout.propTypes = {
  className: PropTypes.string,
  dataHook: PropTypes.string,
  /** @deprecated use closeButtonProps instead. */
  onCloseButtonClick: PropTypes.func,
  /** @deprecated use helpButtonProps instead. */
  onHelpButtonClick: PropTypes.func,
  skin: PropTypes.oneOf(['standard', 'premium']),
  theme: PropTypes.oneOf(['standard', 'premium']),
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  subtitle: PropTypes.string,
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  actionsSize: Button.propTypes.size,
  primaryButtonText: PropTypes.string,
  primaryButtonOnClick: PropTypes.func,
  primaryButtonProps: (() => {
    const { dataHook, ...buttonProps } = Button.propTypes;
    return PropTypes.shape(buttonProps);
  })(),
  secondaryButtonText: PropTypes.string,
  secondaryButtonOnClick: PropTypes.func,
  secondaryButtonProps: (() => {
    const { dataHook, ...buttonProps } = Button.propTypes;
    return PropTypes.shape(buttonProps);
  })(),
  sideActions: PropTypes.node,
  footnote: PropTypes.node,
  footnoteSkin: PropTypes.oneOf(['neutral', 'light']),
  illustration: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  linkText: PropTypes.string,
  linkOnClick: PropTypes.func,
  helpButtonProps: PropTypes.shape({
    onClick: PropTypes.func,
    size: PropTypes.oneOf(['small', 'medium', 'large']),
    skin: PropTypes.oneOf([
      'standard',
      'standardFilled',
      'light',
      'lightFilled',
      'dark',
      'transparent',
    ]),
  }),
  closeButtonProps: PropTypes.shape({
    onClick: PropTypes.func,
    size: PropTypes.oneOf(['small', 'medium', 'large']),
    skin: PropTypes.oneOf([
      'standard',
      'standardFilled',
      'light',
      'lightFilled',
      'dark',
      'transparent',
    ]),
    autoFocus: PropTypes.bool,
  }),
};
export default AnnouncementModalLayout;
